import { useCallback, useEffect, useState } from 'react';
import { Col, Form, Row } from 'antd';
import { AInput, AModal, ASelect } from '../../../../components';
import { IProcedureInsert, IProcedure } from '../../../../shared/interfaces';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../../../../hooks/toast';
import {
  formatToBRL,
  parseCurrencyInput,
} from '../../../../shared/utils/functions';
import { patchTreatmentItem, postTreatmentItem } from '../../../../http';

interface IInsertEditProcedureProps {
  open: boolean;
  close: () => void;
  treatment_id: string | undefined;
  professional_id: string | null;
  code: number | null;
  procedures: IProcedure[];
  setProceduresInsert: React.Dispatch<React.SetStateAction<IProcedureInsert[]>>;
  setProcedureInsert: React.Dispatch<
    React.SetStateAction<IProcedureInsert | null>
  >;
  procedureInsert: IProcedureInsert | null;
}

const InsertEditProcedures: React.FC<IInsertEditProcedureProps> = ({
  open,
  close,
  treatment_id,
  professional_id,
  code,
  procedures,
  setProceduresInsert,
  setProcedureInsert,
  procedureInsert,
}) => {
  const { addToast } = useToast();
  const [procedure_id, setProcedure_id] = useState<string>();
  const [quantity, setQuantity] = useState<number>(0);
  const [value, setValue] = useState<number>(0);

  const insertProcedure = useCallback(async () => {
    if (procedure_id) {
      const valueActual =
        procedures.find((x) => x.id === procedure_id)?.price ?? 0;
      const item: IProcedureInsert = {
        id: uuidv4(),
        procedure_id,
        quantity,
        discount: valueActual - value,
        value: +valueActual,
      };

      if (code && treatment_id !== 'new') {
        const data = {
          ...item,
          treatment_id,
          professional_id,
        };
        await postTreatmentItem(data)
          .then((data) => {
            addToast({
              type: 'success',
              title: 'Sucesso',
              description: 'Procedimento inserido',
            });
            setProceduresInsert((state) => [...state, data]);
          })
          .catch(() => {
            addToast({
              type: 'error',
              title: 'Erro',
              description: 'Erro ao inserir procedimento',
            });
          });
      } else {
        setProceduresInsert((state) => [...state, item]);
        addToast({
          type: 'success',
          title: 'Sucesso',
          description: 'Procedimento inserido',
        });
      }
      close();
      setProcedure_id('');
    }
  }, [
    addToast,
    close,
    code,
    procedure_id,
    procedures,
    professional_id,
    quantity,
    setProceduresInsert,
    treatment_id,
    value,
  ]);

  const editProcedure = useCallback(async () => {
    const valueActual =
      procedures.find((x) => x.id === procedure_id)?.price ?? 0;
    const discountNot = (valueActual - value) * quantity;
    const data = {
      ...procedureInsert,
      discount: discountNot < 0 ? 0 : discountNot,
      value: value !== +valueActual ? value : +valueActual,
      quantity,
      treatment_id,
      professional_id,
    };
    if (code && treatment_id !== 'new') {
      delete data.procedure_name;
      await patchTreatmentItem(procedureInsert?.id, data)
        .then((data) => {
          addToast({
            type: 'info',
            title: 'Alterado',
            description: 'Procedimento alterado',
          });
          setProceduresInsert((state) =>
            state.map((e) => (e.id !== data.id ? e : data)),
          );
        })
        .catch(() => {
          addToast({
            type: 'error',
            title: 'Erro',
            description: 'Erro ao editar procedimento',
          });
        });
    } else {
      setProceduresInsert((state) =>
        state.map((e: any) => (e.id !== data.id ? e : data)),
      );
      addToast({
        type: 'info',
        title: 'Alterado',
        description: 'Procedimento alterado',
      });
    }
    setProcedureInsert(null);
    close();
  }, [
    addToast,
    close,
    code,
    procedureInsert,
    procedure_id,
    procedures,
    professional_id,
    quantity,
    setProcedureInsert,
    setProceduresInsert,
    treatment_id,
    value,
  ]);

  const handleClose = useCallback(() => {
    setProcedure_id('');
    setValue(0);
    close();
  }, []);

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseCurrencyInput(e.target.value);
    setValue(numericValue);
  };

  const loadPriceProcedure = useCallback(() => {
    if (!procedureInsert) {
      const procedure = procedures.find((x) => x.id === procedure_id);
      setValue(parseCurrencyInput(procedure?.price));
      setQuantity(procedure ? 1 : 0);
    }
  }, [procedure_id]);

  useEffect(() => {
    loadPriceProcedure();
  }, [procedure_id]);

  useEffect(() => {
    if (procedureInsert) {
      setProcedure_id(procedureInsert.procedure_id);
      const valueDiscounted =
        +procedureInsert.value - +procedureInsert.discount;
      setValue(valueDiscounted);
      setQuantity(procedureInsert.quantity);
    }
  }, [open]);

  return (
    <AModal
      open={open}
      close={handleClose}
      title={procedureInsert ? 'Editar procedimento' : 'Inserir procedimento'}
      onOk={() => (procedureInsert ? editProcedure() : insertProcedure())}
    >
      <Form layout="vertical">
        <Row>
          <Col span={24}>
            <Form.Item label="Procedimento" required>
              <ASelect
                showSearch
                placeholder="Selecione o procedimento"
                optionFilterProp="children"
                onChange={(e) => {
                  setProcedure_id(e);
                }}
                filterOption={(input, option: any) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                value={procedure_id}
                allowClear
                options={procedures.map((pro) => ({
                  label: pro.description,
                  value: pro.id,
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={12}>
            <Form.Item label="Quantidade" required>
              <AInput
                value={quantity}
                type="number"
                min={1}
                onChange={(e) => setQuantity(+e.target.value)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Valor UN." required>
              <AInput
                min={0}
                value={formatToBRL(value)}
                onChange={handlePriceChange}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </AModal>
  );
};

export default InsertEditProcedures;
